import React, { useEffect, useState } from 'react'
import { DotsThree, Pencil, Trash } from '@phosphor-icons/react'
import { Dropdown, MenuProps } from 'antd'
import {
  useDeleteLeaguePractice,
  useGetLeaguePracticeSchedule,
} from 'api/league/LeaguePractice/leaguePractice'
import CustomAvatar from 'components/global/CustomAvatar'
import Spinner from 'components/Spinner'
import AlertModal from 'helper/AlertModal'
import { convertMinutesToLocalTime } from 'helper/utility'
import moment from 'moment'
import { leaguePracticeStore } from 'state/league/leaguePractice'
import { leagueEventStore } from 'state/league/leagueEvent'
import { LeaguePracticeRespType } from 'types/league/leaguePractice'

const Practices: React.FC = () => {
  const { leagueData } = leagueEventStore(store => ({
    leagueData: store.leagueData,
  }))
  const { setAddPracticeModal, setSelectedPractice, selectedPractice } =
    leaguePracticeStore(store => ({
      setAddPracticeModal: store.setAddPracticeModal,
      setSelectedPractice: store.setSelectedPractice,
      selectedPractice: store.selectedPractice,
    }))

  const [alertModal, setAlertModal] = useState(false)

  const {
    data: practices,
    isLoading,
    isFetching,
    refetch,
  } = useGetLeaguePracticeSchedule({ leagueId: leagueData._id })

  const { mutate: deletePractice, isLoading: deleteLoading } =
    useDeleteLeaguePractice()

  useEffect(() => {
    refetch()
  }, [])

  // Group practices by date
  const grouped = (practices ?? []).reduce<
    Record<string, LeaguePracticeRespType[]>
  >((acc, p) => {
    const dateKey = moment(p.date).format('YYYY-MM-DD')
    if (!acc[dateKey]) acc[dateKey] = []
    acc[dateKey].push(p)
    return acc
  }, {})

  const sortedDates = Object.keys(grouped).sort()

  const dotOptions: MenuProps['items'] = [
    {
      key: 'edit',
      label: (
        <div className="flex align-center gap-16px">
          <Pencil size={20} color="#000" />
          <p>Edit practice</p>
        </div>
      ),
    },
    {
      key: 'delete',
      label: (
        <div className="flex align-center gap-16px">
          <Trash size={20} color="#FF6961" />
          <p>Delete practice</p>
        </div>
      ),
    },
  ]

  const handleMenuClick = (key: string, practice: LeaguePracticeRespType) => {
    if (key === 'edit') {
      setSelectedPractice(practice)
      setAddPracticeModal(true)
    } else if (key === 'delete') {
      setSelectedPractice(practice)
      setAlertModal(true)
    }
  }

  const handleConfirmDelete = () => {
    deletePractice(selectedPractice._id, {
      onSuccess: () => {
        setAlertModal(false)
        setSelectedPractice(null)
      },
      onError: () => setAlertModal(false),
    })
  }

  return (
    <>
      <div className="rounds-container">
        <div className="rounds-main">
          {isLoading || isFetching ? (
            <Spinner />
          ) : sortedDates.length === 0 ? (
            <div className="py-40px flex justify-center">
              <p>No practice games scheduled</p>
            </div>
          ) : (
            sortedDates.map(dateKey => (
              <div className="round-box mt-10" key={dateKey}>
                <h3 className="text-14 w-600">
                  {moment(dateKey).format('dddd, MMMM DD')}
                </h3>
                {grouped[dateKey].map(practice => (
                  <div className="game-box" key={practice._id}>
                    <div className="team-time-container">
                      <div className="team">
                        <CustomAvatar
                          size={40}
                          src={practice.leagueTeam?.team?.profilePhoto?.thumb}
                          name={practice.leagueTeam?.team?.name ?? 'Team'}
                        />
                        <b>{practice.leagueTeam?.team?.name ?? '—'}</b>
                      </div>
                      <div className="time">
                        {practice.courtFieldNumber != null && (
                          <span>Field {practice.courtFieldNumber}</span>
                        )}
                        <b>
                          {convertMinutesToLocalTime(
                            practice.timings?.startTime,
                          )}
                        </b>
                        <span className="text-gray text-12">
                          {practice.duration} min
                        </span>
                      </div>
                    </div>
                    <Dropdown
                      menu={{
                        items: dotOptions,
                        onClick: ({ key }) => handleMenuClick(key, practice),
                      }}
                      trigger={['click']}
                      placement="bottomRight"
                      overlayStyle={{ minWidth: '150px' }}
                    >
                      <DotsThree size={32} color="#000" className="pointer" />
                    </Dropdown>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>

      {alertModal && (
        <AlertModal
          isModalVisible={alertModal}
          setIsModalVisible={setAlertModal}
          data={{
            heading: 'Delete practice',
            message1: 'Are you sure you want to delete this practice game?',
          }}
          onYesClick={handleConfirmDelete}
          loading={deleteLoading}
        />
      )}
    </>
  )
}

export default Practices
